import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import Table from 'apollo-react/components/Table';
import TextField from 'apollo-react/components/TextField';
import SearchIcon from 'apollo-react-icons/Search';
import Trash from 'apollo-react-icons/Trash';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ErrorMessageTextField } from './ErrorMessageTextField';
import { useFormSelector, useTableLastRowVisibilityEffect } from '../../../../../custom-hooks';
import { getUITA } from '../../../../../utils/commonUtils';
import {
  ENTITY_MODEL_EDITOR,
  OPERATION_TYPE_VALIDATION_FIELD_DICTIONARY,
  PARAMETER_TYPE_VALUES,
} from '../../../../constants/editor.constants';
import { ACTION_TYPE_SET_ERROR } from '../../../../constants/runtime.constants';
import { getEntityDefinition, getTranslations } from '../../../../selectors/common.selectors';
import { getEntityDefinitionOptions } from '../../../../utils/editor.utils';
import { getPropTranslation } from '../../../../utils/form-manager.utils';
import TablePropertiesModal from '../TablePropertiesModal';

const useStyles = makeStyles({
  root: {
    overflowY: 'auto',
    maxHeight: '600px',
    '& .selectedRow a, .selectedRow svg, .selectedRow button': {
      color: 'white',
    },

    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },

    '& [class*="MuiToolbar-root"]': {
      padding: '10px 0 0 0 !important',
    },

    '& [class*="MuiTable-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },

    '& [class*="middleAlign"]': {
      height: '60px !important',
    },

    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },

    '& th[class*="InternalTableHeader-resizableHeader"] ': {
      resize: 'none',
    },

    '& [class*="MuiTableCell-root"]:last-child': {
      paddingTop: '.7rem',
      paddingRight: '2rem',
      width: '1.3rem',
    },
  },

  customHeader: {
    justifyContent: 'unset !important',
    alignItems: 'center !important',
    width: '100%',

    '& div': {
      paddingRight: '15px',
    },
  },

  titleHeader: {
    fontSize: 18,
    color: '#000000',
    position: 'relative',
    margin: '0 0 0 10px',
  },
});

const ActionCell = ({ row }) => {
  const { id, onDelete } = row;
  return (
    <IconButton onClick={() => onDelete(id)} aria-label="Delete">
      <Trash />
    </IconButton>
  );
};

const EditableCell = ({ row, column: { header, accessor: key } }) => {
  const {
    id,
    parameterType,
    dummyParameter,
    parameter,
    options,
    editRow,
    handleOpenTablePropertiesModal,
  } = row;
  const [value, setValue] = useState(dummyParameter);

  useEffect(() => {
    if (dummyParameter) {
      setValue(`${dummyParameter} (${parameter})`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameter]);

  switch (parameterType) {
    case 'table':
      return (
        <Select
          size="small"
          fullWidth
          ui-ta={getUITA('Validation Modal Actions', header)}
          canDeselect={false}
          value={row[key]}
          onChange={(e) => {
            editRow(id, key, e.target.value, true);
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );

    case 'unique identifier':
      return (
        <TextField
          size="small"
          fullWidth
          ui-ta={getUITA('Validation Modal Actions', header)}
          value={value}
          onChange={({ target }) => editRow(id, key, target.value)}
          onBlur={() => editRow(id, key, value, true)}
        />
      );

    default:
      return (
        <TextField
          size="small"
          fullWidth
          ui-ta={getUITA('Validation Modal Actions', header)}
          icon={<SearchIcon color="primary" />}
          value={value}
          onChange={({ target }) => editRow(id, key, target.value)}
          onBlur={() => editRow(id, key, value, true)}
          iconProps={{
            onClick: () => {
              handleOpenTablePropertiesModal(key, row);
            },
          }}
        />
      );
  }
};

const makeEditableSelectCell = (options) => ({ row, column: { header, accessor: key } }) => (
  <Select
    size="small"
    fullWidth
    canDeselect={false}
    ui-ta={getUITA('Validation Modal Actions', header)}
    value={row[key] ?? ''}
    disabled={row.expanded ? true : false}
    onChange={(e) => row.editRow(row.id, key, e.target.value, true)}
  >
    {Object.entries(options).map(([key, value]) => {
      return (
        <MenuItem key={key} value={key}>
          {value}
        </MenuItem>
      );
    })}
  </Select>
);

const columns = [
  {
    header: 'Type',
    accessor: 'type',
    customCell: makeEditableSelectCell(OPERATION_TYPE_VALIDATION_FIELD_DICTIONARY),
  },
  {
    header: 'Parameter Type',
    accessor: 'parameterType',
    customCell: makeEditableSelectCell(PARAMETER_TYPE_VALUES),
  },

  {
    header: 'Parameter',
    accessor: 'parameter',
    customCell: EditableCell,
  },
  {
    accessor: 'actiondelete',
    customCell: ActionCell,
  },
];

const ActionsModalValidation = (props) => {
  const classes = useStyles();

  const { name, controlId, ruleId, currentControlModel, setCurrentControlModel } = props;
  const ref = React.useRef(null);

  const controllerUpdateValue = currentControlModel[name];
  const tempPropValue = controllerUpdateValue?.rules;
  const propValue = tempPropValue ? tempPropValue.filter((item) => item.id === ruleId)[0] : [];
  const { errorMsg, validationGroup } = propValue || [];
  const { actions } = propValue && propValue.actions ? propValue : [];
  const [errorMsgValue, setErrorMsgValue] = useState(errorMsg ?? '');
  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const currentKey = useRef('');

  const [rows, setRows] = useState(actions ? actions : []);

  const entityDefinition = useFormSelector(getEntityDefinition);
  const translations = useFormSelector(getTranslations);

  const tableRef = useRef();

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const handleOpenTablePropertiesModal = (key, row) => {
    currentKey.current = key;
    setIsTablePropertiesModalOpen(true);
    setSelectedRow({ ...row, currentKey: key });
  };

  const options = getEntityDefinitionOptions(entityDefinition);

  const processRows = (rows, ruleActionType) => {
    const processedRows = rows.map((item) => {
      const newObj = {
        ...item,
        dummyType: OPERATION_TYPE_VALIDATION_FIELD_DICTIONARY[item.type] ?? '',
        type: item.type ?? '',
        ruleActionType,
        dummySource: item.source ?? '',
        sourceType: '',
        source: item.source,
        targetType: '',
        targetDataType: '',
        dummyParameterType: PARAMETER_TYPE_VALUES[item.parameterType] ?? '',
        dummyParameter: getPropTranslation(entityDefinition, translations, item.parameter) ?? '',
        parameter: item.parameter ?? '',
        parameterType: item.parameterType ?? '',
        sourceMapping: item.source,
        actionParameterType: 'Control',
      };
      delete newObj.conditionMet;
      return newObj;
    });
    return processedRows;
  };

  useEffect(() => {
    setRows(actions);
    setErrorMsgValue(errorMsg ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId]);

  const updateLocalState = (newRows) => {
    const processedRows = processRows(newRows, name);
    const elementsIndex = controllerUpdateValue.rules.findIndex((element) => element.id === ruleId);

    const newValue = { ...controllerUpdateValue };
    newValue.rules[elementsIndex] = {
      ...newValue.rules[elementsIndex],
      type: name,
      errorMsg: errorMsgValue,
      validationGroup,
      actions: processedRows,
    };

    setRows(processedRows);

    setCurrentControlModel((prev) => ({ ...prev, [name]: newValue }));
  };

  useEffect(() => {
    updateLocalState(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMsgValue]);

  const onDelete = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    updateLocalState(newRows, true);
  };

  const editRow = (id, key, value, stateUpdate) => {
    const newRows = rows.map((row) => (row.id === id ? { ...row, [key]: value } : row));
    setRows(newRows);
    if (stateUpdate) {
      updateLocalState(newRows);
    }
  };

  const addRow = () => {
    const rowId = uuidv4();
    const newAction = {
      id: rowId,
      type: ACTION_TYPE_SET_ERROR,
      parameterType: '',
      parameter: '',
      controlId,
      ruleId,
    };
    const newRows = [...rows, newAction];
    updateLocalState(newRows);
  };

  const CustomHeader = () => (
    <div className={classes.customHeader}>
      <div>
        <h3 className={classes.titleHeader}>{'Actions'}</h3>
      </div>
      <div>
        <Button variant="text" ui-ta={getUITA('Add Action Button')} onMouseDown={addRow}>
          {'Add Action'}
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <div>
        <ErrorMessageTextField
          ruleId={ruleId}
          errorMsg={errorMsg}
          setErrorMsgValue={setErrorMsgValue}
        />
      </div>
      <div className={classes.root} ui-ta={getUITA('Modal Actions')} ref={tableRef}>
        <Table
          columns={columns.map((column) => ({ ...column, fixedWidth: false }))}
          stripedRows
          ref={ref}
          rows={rows.map((row) => ({
            ...row,
            onDelete,
            editRow,
            handleOpenTablePropertiesModal,
            key: row.id,
            currentKey: currentKey.current,
            options,
            controlId,
            propValue,
            name,
            ruleId,
          }))}
          hidePagination
          initialSortOrder="asc"
          CustomHeader={CustomHeader}
        />
      </div>
      {isTablePropertiesModalOpen && (
        <TablePropertiesModal
          type={ENTITY_MODEL_EDITOR}
          row={selectedRow}
          handleUpdate={(id, key, value) => {
            editRow(id, key, value, true);
          }}
          onClose={() => {
            setIsTablePropertiesModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ActionsModalValidation;
